import React from 'react';
import { Box, CircularProgress, Alert, useTheme } from '@mui/material';
import { ElectricBolt as ElectricBoltIcon } from '@mui/icons-material';
import { siteConfigService, machinesService } from '../../services/api';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { SiteConfigUnitData, MachineSiteConfigResponse } from '../../types';

// Utility function to get battery color based on SOC level
const getBatteryColor = (soc: number, theme: any): string => {
  if (soc >= 30) return theme.palette.success.main; // Green - high charge
  if (soc >= 15) return theme.palette.warning.light; // Orange - medium charge
  if (soc >= 10) return theme.palette.warning.main; // Red-orange - low charge
  return theme.palette.error.main; // Red - very low charge
};

// Utility function to get SOC text color based on SOC level
const getSOCTextColor = (soc: number, theme: any): string => {
  return soc > 55 ? 'white' : theme.palette.divider;
};

// Utility function to calculate SOC text position
const getSOCTextPosition = (soc: number, fillTop: number, fillHeight: number): number => {
  if (soc > 55) {
    // Position just below the top of the colored area for high SOC
    return fillTop + 16;
  } else {
    // Position just above the top of the colored area for low SOC
    return fillTop - 8;
  }
};

// Interface for battery data
interface BatteryData {
  centralBatterySOC: number;
  battery2SOC: number;
  battery1SOC: number; // CCS1 EV SOC
  centralBatteryVoltage: number;
  centralBatteryEnergy: number;
  centralBatteryCurrent: number;
  battery2Voltage: number;
  battery2Power: number;
  battery2Current: number;
}

// Static SVG components for different device configurations

// ThorVariantSVG removed (unused)

type BoostChargerVariantProps = {
  width: number;
  height: number;
  batteryData?: BatteryData;
  siteConfigData?: SiteConfigUnitData;
  showBattery: boolean;
  showAC: boolean;
  showCCS1: boolean;
  showCCS2: boolean;
  zoomIn?: boolean;
  centerVertical?: boolean;
  shouldConvertToKW?: boolean;
};

const BoostChargerVariantSVG: React.FC<BoostChargerVariantProps> = ({ width, height, batteryData, siteConfigData, showBattery, showAC, showCCS1, showCCS2, zoomIn = false, centerVertical = false, shouldConvertToKW = false }) => {
  const theme = useTheme();
  // Adjust viewBox for zoom - if zoomIn is true, use a smaller viewBox area to zoom in
  const viewBoxWidth = 400;
  const viewBoxHeight = zoomIn ? 250 : 300;
  // If centerVertical is true, use a less negative Y offset to center the content vertically
  const viewBoxY = zoomIn ? (centerVertical ? 20 : -100) : 0;
  const scale = Math.min(width / viewBoxWidth, height / viewBoxHeight);
  const scaledWidth = viewBoxWidth * scale;
  const scaledHeight = viewBoxHeight * scale;
  
  const centralSOC = batteryData?.centralBatterySOC ?? 99;
  const battery2SOC = batteryData?.battery2SOC ?? 95;
  const battery1SOC = batteryData?.battery1SOC ?? 80; // CCS1 EV SOC
  const centralVoltage = batteryData?.centralBatteryVoltage ?? 748;
  const centralEnergy = batteryData?.centralBatteryEnergy ?? 374;
  const centralCurrent = batteryData?.centralBatteryCurrent ?? 0;
  
  // Utility function to convert power from W to kW for KE chargers
  const convertPowerToKW = (value: number | null | undefined): number | null => {
    if (value === null || value === undefined) return null;
    return shouldConvertToKW ? value / 1000 : value;
  };
  
  const gridVoltage = siteConfigData?.grid_input?.[0]?.input_voltage;
  const gridPower = convertPowerToKW(siteConfigData?.grid_input?.[0]?.input_power);
  
  const acVoltage = siteConfigData?.ac?.[0]?.ac_voltage;
  const acPower = siteConfigData?.ac?.[0]?.ac_power;
  
  const ccs1Connected = siteConfigData?.ccs1?.[0]?.ccs1_connected;
  const ccs1Voltage = siteConfigData?.ccs1?.[0]?.ccs1_voltage;
  const ccs1Power = convertPowerToKW(siteConfigData?.ccs1?.[0]?.ccs1_power);
  const ccs1Current = siteConfigData?.ccs1?.[0]?.ccs1_current;
  
  const ccs2Connected = siteConfigData?.ccs2?.[0]?.ccs2_connected;
  const ccs2Voltage = siteConfigData?.ccs2?.[0]?.ccs2_voltage;
  const ccs2Power = convertPowerToKW(siteConfigData?.ccs2?.[0]?.ccs2_power);
  const ccs2Current = siteConfigData?.ccs2?.[0]?.ccs2_current;
  
  const centralBatteryColor = getBatteryColor(centralSOC, theme);
  const battery2Color = getBatteryColor(battery2SOC, theme);
  const battery1Color = getBatteryColor(battery1SOC, theme);
  const isType2Layout = showBattery && showCCS1 && !showCCS2;
  const isType1Layout = showAC && !showBattery && !showCCS1 && !showCCS2;
  // For TYPE1, center the grid and AC horizontally while maintaining original line length of 120
  // Original: Grid right edge at 60, AC left edge at 180, line length = 120
  // To center: Grid right edge at 140, AC left edge at 260, line length = 120
  const gridX = isType1Layout ? 100 : 20; // Grid icon is 40px wide, so 140 - 40 = 100
  const acX = isType1Layout ? 260 : 340; // AC left edge at 260
  const targetX = showAC ? acX : (showCCS2 ? 260 : (showCCS1 ? 140 : (showBattery ? 200 : acX)));

  // Adjust Y offsets when zooming in to move content up
  // If centerVertical is true, use more centered positions (less negative/more positive)
  const gridYOffset = zoomIn ? (centerVertical ? 110 : -30) : 130;
  const batteryYOffset = zoomIn ? (centerVertical ? 30 : -110) : 50;
  const acYOffset = zoomIn ? (centerVertical ? 110 : -30) : 130;
  const ccs1YOffset = zoomIn ? (centerVertical ? 170 : 30) : 200;
  const ccs2YOffset = zoomIn ? (centerVertical ? 170 : 30) : 200;
  const connectionLineY = zoomIn ? (centerVertical ? 130 : -10) : 150;
  const connectionLineY2 = zoomIn ? (centerVertical ? 150 : 10) : 170;
  
  return (
    <svg width={scaledWidth} height={scaledHeight} viewBox={`0 ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`} style={{ maxWidth: '100%', height: 'auto' }}>
      {/* Grid Input */}
      <g transform={`translate(${gridX}, ${gridYOffset})`}>
        <defs>
          <filter id="gridShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.3)"/>
          </filter>
        </defs>
        <rect x="0" y="0" width="40" height="40" fill="white" filter="url(#gridShadow)" rx="8"/>
        <image href={process.env.PUBLIC_URL + "/grid.svg"} x="10" y="10" width="20" height="20"/>
        <text x="20" y="55" fontSize="8" fill="#000" textAnchor="middle">{gridVoltage !== null && gridVoltage !== undefined ? `${Math.round(gridVoltage)} V` : 'N/A'}</text>
        <text x="20" y="65" fontSize="8" fill="#000" textAnchor="middle">{gridPower !== null && gridPower !== undefined ? `${Math.round(gridPower)} kW` : 'N/A'}</text>
      </g>
      
      {/* Central Battery */}
      {showBattery && (
        <g transform={`translate(170, ${batteryYOffset})`}>
          <defs>
            <filter id="batteryShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.3)"/>
            </filter>
          </defs>
          <rect x="24" y="0" width="12" height="4" fill="white" filter="url(#batteryShadow)" rx="4"/>
          <rect x="0" y="4" width="60" height="80" fill="white" filter="url(#batteryShadow)" rx="6"/>
          <rect 
            x="4" 
            y={8 + 72 - (72 * (centralSOC ?? 0) / 100)} 
            width="52" 
            height={72 * (centralSOC ?? 0) / 100} 
            fill={centralBatteryColor} 
            rx="2"
          />
          <text x="30" y={getSOCTextPosition(centralSOC ?? 0, 8 + 72 - (72 * (centralSOC ?? 0) / 100), 72 * (centralSOC ?? 0) / 100)} fontSize="16" fill={getSOCTextColor(centralSOC ?? 0, theme)} textAnchor="middle" fontWeight="bold" >{centralSOC !== null && centralSOC !== undefined ? `${Math.round(centralSOC)} %` : 'N/A'}</text>
          <text x="70" y="20" fontSize="8" fill="#000">{centralVoltage !== null && centralVoltage !== undefined ? `${Math.round(centralVoltage)} V` : 'N/A'}</text>
          <text x="70" y="30" fontSize="8" fill="#000">{centralEnergy !== null && centralEnergy !== undefined ? `${Math.round(centralEnergy)} kWh` : 'N/A'}</text>
          <text x="70" y="40" fontSize="8" fill="#000">{centralCurrent !== null && centralCurrent !== undefined ? `${Math.round(centralCurrent)} A` : 'N/A'}</text>
        </g>
      )}
      
      {/* House Load */}
      {showAC && (
        <g transform={`translate(${acX}, ${acYOffset})`}>
          <defs>
            <filter id="houseShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.3)"/>
            </filter>
          </defs>
          <rect x="0" y="0" width="40" height="40" fill="white" filter="url(#houseShadow)" rx="8"/>
          <image href={process.env.PUBLIC_URL + "/outlet.svg"} x="10" y="10" width="20" height="20"/>
          <text x="20" y="55" fontSize="8" fill="#000" textAnchor="middle">{acVoltage !== null && acVoltage !== undefined ? `${Math.round(acVoltage)} V` : 'N/A'}</text>
          <text x="20" y="65" fontSize="8" fill="#000" textAnchor="middle">{acPower !== null && acPower !== undefined ? `${Math.round(acPower)} kW` : 'N/A'}</text>
        </g>
      )}
      
      {/* CCS1 */}
      {showCCS1 && (
        ccs1Connected ? (
          <g transform={`translate(${isType2Layout ? 179 : 120}, ${ccs1YOffset})`}>
            <defs>
              <filter id="ccs1Shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.3)"/>
              </filter>
            </defs>
            <text x="0" y="-5" fontSize="10" fill={theme.palette.text.secondary} textAnchor="middle" fontWeight="500">CCS1</text>
            <rect x="17" y="0" width="8" height="3" fill="white" filter="url(#ccs1Shadow)" rx="3"/>
            <rect x="0" y="3" width="42" height="56" fill="white" filter="url(#ccs1Shadow)" rx="5"/>
            <rect 
              x="3" 
              y={6 + 50 - (50 * (battery1SOC ?? 0) / 100)} 
              width="36" 
              height={50 * (battery1SOC ?? 0) / 100} 
              fill={battery1Color} 
              rx="1"
            />
            <text x="21" y={getSOCTextPosition(battery1SOC ?? 0, 6 + 50 - (50 * (battery1SOC ?? 0) / 100), 50 * (battery1SOC ?? 0) / 100)} fontSize="11" fill={getSOCTextColor(battery1SOC ?? 0, theme)} textAnchor="middle" fontWeight="bold" >{Math.round(battery1SOC ?? 0)}%</text>
            <text x="50" y="15" fontSize="8" fill="#000">{ccs1Voltage !== null && ccs1Voltage !== undefined ? `${Math.round(ccs1Voltage)} V` : 'N/A'}</text>
            <text x="50" y="25" fontSize="8" fill="#000">{ccs1Power !== null && ccs1Power !== undefined ? `${Math.round(ccs1Power)} kW` : 'N/A'}</text>
            <text x="50" y="35" fontSize="8" fill="#000">{ccs1Current !== null && ccs1Current !== undefined ? `${Math.round(ccs1Current)} A` : 'N/A'}</text>
            <text x="21" y="75" fontSize="10" fill="#55B077" textAnchor="middle" fontWeight="500">Connected</text>
          </g>
        ) : (
          <g transform={`translate(${isType2Layout ? 179 : 120}, ${ccs1YOffset})`}>
            <defs>
              <filter id="ccs1NotConnectedShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.3)"/>
              </filter>
            </defs>
            <text x="0" y="-5" fontSize="10" fill={theme.palette.text.secondary} textAnchor="middle" fontWeight="500">CCS1</text>
            <rect x="0" y="0" width="40" height="40" fill="white" filter="url(#ccs1NotConnectedShadow)" rx="8"/>
            <image href={process.env.PUBLIC_URL + "/Chargers.svg"} x="10" y="10" width="20" height="20"/>
            <text x="20" y="55" fontSize="8" fill={theme.palette.text.secondary} textAnchor="middle">Not connected</text>
          </g>
        )
      )}
      
      {/* CCS2 */}
      {showCCS2 && (
        ccs2Connected ? (
          <g transform={`translate(239, ${ccs2YOffset})`}>
            <defs>
              <filter id="ccs2Shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.3)"/>
              </filter>
            </defs>
            <text x="41" y="-5" fontSize="10" fill={theme.palette.text.secondary} textAnchor="middle" fontWeight="500">CCS2</text>
            <rect x="17" y="0" width="8" height="3" fill="white" filter="url(#ccs2Shadow)" rx="3"/>
            <rect x="0" y="3" width="42" height="56" fill="white" filter="url(#ccs2Shadow)" rx="5"/>
            <rect 
              x="3" 
              y={6 + 50 - (50 * battery2SOC / 100)} 
              width="36" 
              height={50 * battery2SOC / 100} 
              fill={battery2Color} 
              rx="1"
            />
            <text x="21" y={getSOCTextPosition(battery2SOC ?? 0, 6 + 50 - (50 * (battery2SOC ?? 0) / 100), 50 * (battery2SOC ?? 0) / 100)} fontSize="11" fill={getSOCTextColor(battery2SOC ?? 0, theme)} textAnchor="middle" fontWeight="bold" >{Math.round(battery2SOC ?? 0)}%</text>
            <text x="50" y="15" fontSize="8" fill="#000">{ccs2Voltage !== null && ccs2Voltage !== undefined ? `${Math.round(ccs2Voltage)} V` : 'N/A'}</text>
            <text x="50" y="25" fontSize="8" fill="#000">{ccs2Power !== null && ccs2Power !== undefined ? `${Math.round(ccs2Power)} kW` : 'N/A'}</text>
            <text x="50" y="35" fontSize="8" fill="#000">{ccs2Current !== null && ccs2Current !== undefined ? `${Math.round(ccs2Current)} A` : 'N/A'}</text>
            <text x="21" y="75" fontSize="10" fill="#55B077" textAnchor="middle" fontWeight="500">Connected</text>
          </g>
        ) : (
          <g transform={`translate(239, ${ccs2YOffset})`}>
            <defs>
              <filter id="ccs2NotConnectedShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.3)"/>
              </filter>
            </defs>
            <text x="40" y="-5" fontSize="10" fill={theme.palette.text.secondary} textAnchor="middle" fontWeight="500">CCS2</text>
            <rect x="0" y="0" width="40" height="40" fill="white" filter="url(#ccs2NotConnectedShadow)" rx="8"/>
            <image href={process.env.PUBLIC_URL + "/Chargers.svg"} x="10" y="10" width="20" height="20"/>
            <text x="20" y="55" fontSize="8" fill={theme.palette.text.secondary} textAnchor="middle">Not connected</text>
          </g>
        )
      )}
      
      {/* Connection lines */}
      {/* For TYPE3 (no battery, no AC, both CCS), grid line goes horizontally then vertically down to middle of horizontal line between CCS1 and CCS2 */}
      {!showBattery && !showAC && showCCS1 && showCCS2 ? (
        <path d={`M ${gridX + 40} ${connectionLineY} L 200 ${connectionLineY} L 200 ${connectionLineY2}`} stroke={theme.palette.divider} strokeWidth="2" fill="none" strokeDasharray="5,5"/>
      ) : (
        <path d={`M ${gridX + 40} ${connectionLineY} L ${targetX} ${connectionLineY}`} stroke={theme.palette.divider} strokeWidth="2" fill="none" strokeDasharray="5,5"/>
      )}
      {showBattery && (
        isType2Layout 
          ? <path d={`M 200 ${batteryYOffset + 84} L 200 ${ccs1YOffset}`} stroke={theme.palette.divider} strokeWidth="2" fill="none" strokeDasharray="5,5"/>
          : <path d={`M 200 ${batteryYOffset + 84} L 200 ${connectionLineY2}`} stroke={theme.palette.divider} strokeWidth="2" fill="none" strokeDasharray="5,5"/>
      )}
      {!isType2Layout && (showCCS1 || showCCS2) && <path d={`M 200 ${connectionLineY2} L 140 ${connectionLineY2}`} stroke={theme.palette.divider} strokeWidth="2" fill="none" strokeDasharray="5,5"/>}
      {!isType2Layout && showCCS1 && <path d={`M 140 ${connectionLineY2} L 140 ${ccs1YOffset}`} stroke={theme.palette.divider} strokeWidth="2" fill="none" strokeDasharray="5,5"/>}
      {showCCS2 && <path d={`M 200 ${connectionLineY2} L 260 ${connectionLineY2}`} stroke={theme.palette.divider} strokeWidth="2" fill="none" strokeDasharray="5,5"/>}
      {showCCS2 && <path d={`M 260 ${connectionLineY2} L 260 ${ccs2YOffset}`} stroke={theme.palette.divider} strokeWidth="2" fill="none" strokeDasharray="5,5"/>}
    </svg>
  );
};

// Excavator SVG component
type ExcavatorVariantProps = {
  width: number;
  height: number;
  batteryData?: BatteryData;
  siteConfigData?: SiteConfigUnitData;
  shouldConvertToKW?: boolean;
};

const ExcavatorVariantSVG: React.FC<ExcavatorVariantProps> = ({ width, height, batteryData, siteConfigData, shouldConvertToKW = false }) => {
  const theme = useTheme();
  const viewBoxWidth = 400;
  const viewBoxHeight = 300;
  const scale = Math.min(width / viewBoxWidth, height / viewBoxHeight);
  const scaledWidth = viewBoxWidth * scale;
  const scaledHeight = viewBoxHeight * scale;
  
  const centralSOC = batteryData?.centralBatterySOC ?? 50;
  const centralVoltage = batteryData?.centralBatteryVoltage ?? 400;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const centralEnergy = batteryData?.centralBatteryEnergy ?? 100;
  const centralCurrent = batteryData?.centralBatteryCurrent ?? 0;
  
  // Utility function to convert power from W to kW
  const convertPowerToKW = (value: number | null | undefined): number | null => {
    if (value === null || value === undefined) return null;
    return shouldConvertToKW ? value / 1000 : value;
  };
  
  const gridVoltage = siteConfigData?.grid_input?.[0]?.input_voltage;
  const gridPower = convertPowerToKW(siteConfigData?.grid_input?.[0]?.input_power);
  
  // Charger data (using CCS1 data as charger data)
  const chargerVoltage = siteConfigData?.ccs1?.[0]?.ccs1_voltage;
  const chargerPower = convertPowerToKW(siteConfigData?.ccs1?.[0]?.ccs1_power);
  const chargerCurrent = siteConfigData?.ccs1?.[0]?.ccs1_current;
  
  // Motor data (using motor data from site config)
  const motorSpeed = siteConfigData?.motor?.[0]?.motor_speed ?? null;
  
  const centralBatteryColor = getBatteryColor(centralSOC, theme);
  
  // Position definitions
  const gridX = 20;
  const chargerX = 20;
  const batteryX = 200;
  const batteryY = 100;
  const batteryLineY = batteryY + 44; // Middle of battery (4 + 80/2 = 44)
  
  // Grid and charger positioned with equal vertical distance to battery
  // Battery center is at batteryLineY = 144
  // Use equal distance of 50 pixels above and below battery center
  const gridLineY = batteryLineY - 50; // 50 pixels above battery center
  const chargerLineY = batteryLineY + 50; // 50 pixels below battery center
  const gridY = gridLineY - 20; // Grid icon Y position (center - half icon height)
  const chargerY = chargerLineY - 20; // Charger icon Y position (center - half icon height)
  
  // Motor Y position adjusted so horizontal line from battery hits it in the middle
  // batteryLineY = 144, so motor center should be at 144
  // motor center = motorY + 20, so motorY = 144 - 20 = 124
  const motorX = 300;
  const motorY = 124;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const motorLineY = motorY + 20; // Middle of motor icon (should equal batteryLineY)
  
  return (
    <svg width={scaledWidth} height={scaledHeight} viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`} style={{ maxWidth: '100%', height: 'auto' }}>
      {/* Grid Input */}
      <g transform={`translate(${gridX}, ${gridY})`}>
        <defs>
          <filter id="excavatorGridShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.3)"/>
          </filter>
        </defs>
        <rect x="0" y="0" width="40" height="40" fill="white" filter="url(#excavatorGridShadow)" rx="8"/>
        <image href={process.env.PUBLIC_URL + "/grid.svg"} x="10" y="10" width="20" height="20"/>
        <text x="20" y="55" fontSize="8" fill="#000" textAnchor="middle">{gridVoltage !== null && gridVoltage !== undefined ? `${Math.round(gridVoltage)} V` : 'N/A'}</text>
        <text x="20" y="65" fontSize="8" fill="#000" textAnchor="middle">{gridPower !== null && gridPower !== undefined ? `${Math.round(gridPower)} kW` : 'N/A'}</text>
      </g>
      
      {/* Charger Node */}
      <g transform={`translate(${chargerX}, ${chargerY})`}>
        <defs>
          <filter id="excavatorChargerShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.3)"/>
          </filter>
        </defs>
        <rect x="0" y="0" width="40" height="40" fill="white" filter="url(#excavatorChargerShadow)" rx="8"/>
        <image href={process.env.PUBLIC_URL + "/Chargers.svg"} x="10" y="10" width="20" height="20"/>
        <text x="20" y="55" fontSize="8" fill="#000" textAnchor="middle">{chargerVoltage !== null && chargerVoltage !== undefined ? `${Math.round(chargerVoltage)} V` : 'N/A'}</text>
        <text x="20" y="65" fontSize="8" fill="#000" textAnchor="middle">{chargerPower !== null && chargerPower !== undefined ? `${Math.round(chargerPower)} kW` : 'N/A'}</text>
        <text x="20" y="75" fontSize="8" fill="#000" textAnchor="middle">{chargerCurrent !== null && chargerCurrent !== undefined ? `${Math.round(chargerCurrent)} A` : 'N/A'}</text>
      </g>
      
      {/* Central Battery */}
      <g transform={`translate(${batteryX}, ${batteryY})`}>
        <defs>
          <filter id="excavatorBatteryShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.3)"/>
          </filter>
        </defs>
        <rect x="24" y="0" width="12" height="4" fill="white" filter="url(#excavatorBatteryShadow)" rx="4"/>
        <rect x="0" y="4" width="60" height="80" fill="white" filter="url(#excavatorBatteryShadow)" rx="6"/>
        <rect 
          x="4" 
          y={8 + 72 - (72 * (centralSOC ?? 0) / 100)} 
          width="52" 
          height={72 * (centralSOC ?? 0) / 100} 
          fill={centralBatteryColor} 
          rx="2"
        />
        <text x="30" y={getSOCTextPosition(centralSOC ?? 0, 8 + 72 - (72 * (centralSOC ?? 0) / 100), 72 * (centralSOC ?? 0) / 100)} fontSize="16" fill={getSOCTextColor(centralSOC ?? 0, theme)} textAnchor="middle" fontWeight="bold" >{centralSOC !== null && centralSOC !== undefined ? `${Math.round(centralSOC)} %` : 'N/A'}</text>
        <text x="30" y="95" fontSize="8" fill="#000" textAnchor="middle">{centralVoltage !== null && centralVoltage !== undefined ? `${Math.round(centralVoltage)} V` : 'N/A'}</text>
        <text x="30" y="105" fontSize="8" fill="#000" textAnchor="middle">{centralCurrent !== null && centralCurrent !== undefined ? `${Math.round(centralCurrent)} A` : 'N/A'}</text>
      </g>
      
      {/* Motor Node (looks like charger but with electric bolt icon) */}
      <g transform={`translate(${motorX}, ${motorY})`}>
        <defs>
          <filter id="excavatorMotorShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.3)"/>
          </filter>
        </defs>
        <rect x="0" y="0" width="40" height="40" fill="white" filter="url(#excavatorMotorShadow)" rx="8"/>
        <foreignObject x="10" y="10" width="20" height="20">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <ElectricBoltIcon sx={{ fontSize: 20, color: 'rgba(0,0,0,0.6)' }} />
          </Box>
        </foreignObject>
        <text x="20" y="55" fontSize="8" fill="#000" textAnchor="middle">{motorSpeed !== null && motorSpeed !== undefined ? `${Math.round(motorSpeed)} RPM` : 'N/A'}</text>
      </g>
      
      {/* Connection lines */}
      {/* Grid to Battery - horizontal then vertical */}
      <path d={`M ${gridX + 40} ${gridLineY} L ${batteryX - 40} ${gridLineY} L ${batteryX - 40} ${batteryLineY} L ${batteryX} ${batteryLineY}`} stroke={theme.palette.divider} strokeWidth="2" fill="none" strokeDasharray="5,5"/>
      
      {/* Charger to Battery - horizontal then vertical */}
      <path d={`M ${chargerX + 40} ${chargerLineY} L ${batteryX - 40} ${chargerLineY} L ${batteryX - 40} ${batteryLineY} L ${batteryX} ${batteryLineY}`} stroke={theme.palette.divider} strokeWidth="2" fill="none" strokeDasharray="5,5"/>
      
      {/* Battery to Motor - horizontal line */}
      <path d={`M ${batteryX + 60} ${batteryLineY} L ${motorX} ${batteryLineY}`} stroke={theme.palette.divider} strokeWidth="2" fill="none" strokeDasharray="5,5"/>
    </svg>
  );
};

// Default and BoostCharger SVGs removed per requirement

interface StaticSiteConfigProps {
  deviceType: 'battery' | 'charger' | 'excavator';
  deviceId: string;
  deviceName: string;
  modelName?: string;
  orgId?: string;
}

const StaticSiteConfig: React.FC<StaticSiteConfigProps> = ({ 
  deviceType, 
  deviceId, 
  deviceName,
  modelName,
  orgId 
}) => {
  console.log('StaticSiteConfig props:', { deviceType, deviceId, deviceName, modelName });
  
  // Get container dimensions for responsive scaling
  const [containerDimensions, setContainerDimensions] = React.useState({ width: 400, height: 300 });
  const containerRef = React.useRef<HTMLDivElement>(null);
  
  // Battery data state
  const [batteryData, setBatteryData] = React.useState<BatteryData | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  
  // Site config data state
  const [siteConfigData, setSiteConfigData] = React.useState<SiteConfigUnitData | null>(null);
  
  // Theme
  const theme = useTheme();

  // Check if charger name starts with "KE" (for power conversion)
  const shouldConvertToKW = React.useMemo(() => {
    return deviceName?.startsWith('KE') ?? false;
  }, [deviceName]);

  React.useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setContainerDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Fetch site config data from the new API endpoint
  React.useEffect(() => {
    const fetchSiteConfigData = async () => {
      if (!deviceId || !orgId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        console.log('Fetching site config data for unit:', deviceId, 'org:', orgId, 'deviceType:', deviceType);
        
        // Use machine site config API for excavators
        if (deviceType === 'excavator') {
          const response = await machinesService.getMachineSiteConfig(orgId, deviceId);
          
          if (response.success && response.data && response.data.data && response.data.data.length > 0) {
            console.log('Machine site config data received:', response.data);
            
            // Map machine site config response to SiteConfigUnitData structure
            const machineData = response.data.data[0];
            
            // Helper function to parse string numbers
            const parseNumber = (value: string | null | undefined): number | null => {
              if (value === null || value === undefined || value === '') return null;
              const parsed = parseFloat(value);
              return isNaN(parsed) ? null : parsed;
            };
            
            const parseBoolean = (value: string | boolean | null | undefined): boolean => {
              if (typeof value === 'boolean') return value;
              if (value === null || value === undefined) return false;
              return value === 'true' || value === '1';
            };
            
            // Map to SiteConfigUnitData structure
            const mappedData: SiteConfigUnitData = {
              battery: [{
                availability: machineData.battery_availability,
                battery_soc: parseNumber(machineData.battery_soc) ?? 0,
                battery_voltage: parseNumber(machineData.battery_voltage) ?? 0,
                battery_kwh: 0, // Not available in machine API, default to 0
                battery_current: parseNumber(machineData.battery_current),
              }],
              grid_input: [{
                input_voltage: null, // Not available in machine API
                input_power: null, // Not available in machine API
              }],
              ccs1: [{
                ccs1_connected: parseBoolean(machineData.ccs_connected),
                ccs1_voltage: parseNumber(machineData.ccs_voltage),
                ccs1_power: parseNumber(machineData.ccs_power),
                ccs1_current: parseNumber(machineData.ccs_current),
                ccs1_ev_soc: null, // Not available in machine API
              }],
              ccs2: [{
                ccs2_connected: false, // Not available in machine API
                ccs2_voltage: null, // Not available in machine API
                ccs2_current: null, // Not available in machine API
                ccs2_power: null, // Not available in machine API
                ccs2_ev_soc: null, // Not available in machine API
              }],
              motor: [{
                motor_voltage: null,
                motor_power: null,
                motor_current: null,
                motor_speed: parseNumber(machineData.motor_speed),
              }],
              ac: [{
                ac_voltage: null, // Not available in machine API
                ac_power: parseNumber(machineData.ac_power),
              }],
            };
            
            console.log('Mapped machine site config data:', mappedData);
            setSiteConfigData(mappedData);
          } else {
            console.error('Machine site config API failed:', response);
            setError('Failed to load machine site config data');
          }
        } else {
          // Use regular site config API for chargers and batteries
          const response = await siteConfigService.getSiteConfigUnit(deviceId, orgId);
          
          if (response.success && response.data) {
            console.log('Site config data received:', response.data);
            setSiteConfigData(response.data);
          } else {
            console.error('Site config API failed:', response);
            setError('Failed to load site config data');
          }
        }
      } catch (err) {
        console.error('Failed to fetch site config data:', err);
        setError('Failed to load site config data');
      } finally {
        setLoading(false);
      }
    };

    fetchSiteConfigData();
  }, [deviceId, orgId, deviceType]);

  // Process battery data from site config API
  React.useEffect(() => {
    const processBatteryData = () => {
      if (!siteConfigData) return;
      
      try {
        // Get all data from site config API
        const batteryVoltage = siteConfigData?.battery?.[0]?.battery_voltage;
        const batteryEnergy = siteConfigData?.battery?.[0]?.battery_kwh;
        const batteryCurrent = siteConfigData?.battery?.[0]?.battery_current;
        const batterySOC = siteConfigData?.battery?.[0]?.battery_soc;
        const ccs1EVSOC = siteConfigData?.ccs1?.[0]?.ccs1_ev_soc;
        const ccs2Voltage = siteConfigData?.ccs2?.[0]?.ccs2_voltage;
        const ccs2Power = siteConfigData?.ccs2?.[0]?.ccs2_power;
        const ccs2Current = siteConfigData?.ccs2?.[0]?.ccs2_current;
        const ccs2EVSOC = siteConfigData?.ccs2?.[0]?.ccs2_ev_soc;

        // Create battery data from site config values
        const batteryData: BatteryData = {
          centralBatterySOC: batterySOC ?? 0,
          battery2SOC: ccs2EVSOC !== null && ccs2EVSOC !== undefined ? Math.round(ccs2EVSOC) : 0,
          battery1SOC: ccs1EVSOC !== null && ccs1EVSOC !== undefined ? Math.round(ccs1EVSOC) : 0,
          centralBatteryVoltage: batteryVoltage ?? 0,
          centralBatteryEnergy: batteryEnergy ?? 0,
          centralBatteryCurrent: batteryCurrent ?? 0,
          battery2Voltage: ccs2Voltage ?? 0,
          battery2Power: ccs2Power ?? 0,
          battery2Current: ccs2Current ?? 0,
        };
        
        console.log('Battery data set from site config:', {
          centralBatterySOC: batterySOC !== null && batterySOC !== undefined ? `${batterySOC}%` : 'N/A',
          battery1SOC: `${batteryData.battery1SOC}%`,
          battery2SOC: `${batteryData.battery2SOC}%`,
          centralBatteryVoltage: batteryVoltage !== null && batteryVoltage !== undefined ? `${batteryVoltage} V` : 'N/A',
          centralBatteryEnergy: batteryEnergy !== null && batteryEnergy !== undefined ? `${batteryEnergy} kWh` : 'N/A',
          centralBatteryCurrent: batteryCurrent !== null && batteryCurrent !== undefined ? `${batteryCurrent} A` : 'N/A',
          gridVoltage: siteConfigData?.grid_input?.[0]?.input_voltage !== null && siteConfigData?.grid_input?.[0]?.input_voltage !== undefined ? `${siteConfigData.grid_input[0].input_voltage} V` : 'N/A',
          gridPower: siteConfigData?.grid_input?.[0]?.input_power !== null && siteConfigData?.grid_input?.[0]?.input_power !== undefined ? `${siteConfigData.grid_input[0].input_power} kW` : 'N/A',
          acVoltage: siteConfigData?.ac?.[0]?.ac_voltage !== null && siteConfigData?.ac?.[0]?.ac_voltage !== undefined ? `${siteConfigData.ac[0].ac_voltage} V` : 'N/A',
          acPower: siteConfigData?.ac?.[0]?.ac_power !== null && siteConfigData?.ac?.[0]?.ac_power !== undefined ? `${siteConfigData.ac[0].ac_power} kW` : 'N/A',
          ccs1Connected: siteConfigData?.ccs1?.[0]?.ccs1_connected !== null && siteConfigData?.ccs1?.[0]?.ccs1_connected !== undefined ? siteConfigData.ccs1[0].ccs1_connected : 'N/A',
          ccs1EVSOC: ccs1EVSOC !== null && ccs1EVSOC !== undefined ? `${ccs1EVSOC}%` : 'N/A',
          ccs1Voltage: siteConfigData?.ccs1?.[0]?.ccs1_voltage !== null && siteConfigData?.ccs1?.[0]?.ccs1_voltage !== undefined ? `${siteConfigData.ccs1[0].ccs1_voltage} V` : 'N/A',
          ccs1Power: siteConfigData?.ccs1?.[0]?.ccs1_power !== null && siteConfigData?.ccs1?.[0]?.ccs1_power !== undefined ? `${siteConfigData.ccs1[0].ccs1_power} kW` : 'N/A',
          ccs1Current: siteConfigData?.ccs1?.[0]?.ccs1_current !== null && siteConfigData?.ccs1?.[0]?.ccs1_current !== undefined ? `${siteConfigData.ccs1[0].ccs1_current} A` : 'N/A',
          ccs2Connected: siteConfigData?.ccs2?.[0]?.ccs2_connected !== null && siteConfigData?.ccs2?.[0]?.ccs2_connected !== undefined ? siteConfigData.ccs2[0].ccs2_connected : 'N/A',
          ccs2EVSOC: ccs2EVSOC !== null && ccs2EVSOC !== undefined ? `${ccs2EVSOC}%` : 'N/A',
          ccs2Voltage: siteConfigData?.ccs2?.[0]?.ccs2_voltage !== null && siteConfigData?.ccs2?.[0]?.ccs2_voltage !== undefined ? `${siteConfigData.ccs2[0].ccs2_voltage} V` : 'N/A',
          ccs2Power: siteConfigData?.ccs2?.[0]?.ccs2_power !== null && siteConfigData?.ccs2?.[0]?.ccs2_power !== undefined ? `${siteConfigData.ccs2[0].ccs2_power} kW` : 'N/A',
          ccs2Current: siteConfigData?.ccs2?.[0]?.ccs2_current !== null && siteConfigData?.ccs2?.[0]?.ccs2_current !== undefined ? `${siteConfigData.ccs2[0].ccs2_current} A` : 'N/A',
          centralBatteryColor: batterySOC !== null && batterySOC !== undefined ? getBatteryColor(batterySOC, theme) : 'N/A',
          battery1Color: getBatteryColor(batteryData.battery1SOC, theme),
          battery2Color: getBatteryColor(batteryData.battery2SOC, theme)
        });
        
        setBatteryData(batteryData);
        
      } catch (err) {
        console.error('Failed to process battery data from site config:', err);
        setError('Failed to process battery data');
      }
    };

    processBatteryData();
  }, [siteConfigData, theme]);

  const renderSVG = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <CircularProgress size={40} />
        </Box>
      );
    }
    
    if (error) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 2 }}>
          <Alert severity="warning" sx={{ maxWidth: '80%' }}>
            {error}
          </Alert>
        </Box>
      );
    }
    
    // Helper function to check if model name matches (case-insensitive, flexible matching)
    const matchesModel = (name: string | undefined, patterns: string[]): boolean => {
      if (!name) return false;
      const lowerName = name.toLowerCase();
      return patterns.some(pattern => lowerName.includes(pattern.toLowerCase()));
    };
    
    // Log model name and site config data for debugging
    console.log('StaticSiteConfig renderSVG:', {
      modelName,
      deviceType,
      hasSiteConfigData: !!siteConfigData,
      siteConfigData,
      hasBatteryData: !!batteryData,
    });
    
    // TYPE1: Pro -> grid + AC only (BoostCharger-based)
    if (matchesModel(modelName, ['Pro'])) {
      return (
        <BoostChargerVariantSVG
          width={containerDimensions.width}
          height={containerDimensions.height}
          batteryData={batteryData || undefined}
          siteConfigData={siteConfigData || undefined}
          showBattery={false}
          showAC={true}
          showCCS1={false}
          showCCS2={false}
          zoomIn={true}
          centerVertical={true}
          shouldConvertToKW={shouldConvertToKW}
        />
      );
    }

    // TYPE2: Thor / Thor V2 / Hummingbird V2 -> grid + AC + CCS1 + main battery
    if (matchesModel(modelName, ['Thor', 'Hummingbird']) && !matchesModel(modelName, ['V3', 'V3 Nordic'])) {
      return (
        <BoostChargerVariantSVG
          width={containerDimensions.width}
          height={containerDimensions.height}
          batteryData={batteryData || undefined}
          siteConfigData={siteConfigData || undefined}
          showBattery={true}
          showAC={true}
          showCCS1={true}
          showCCS2={false}
          shouldConvertToKW={shouldConvertToKW}
        />
      );
    }

    // TYPE3: Mjølner / Boostpoint -> grid + CCS1 + CCS2
    if (matchesModel(modelName, ['Mjølner', 'Mjolner', 'Boostpoint'])) {
      return (
        <BoostChargerVariantSVG
          width={containerDimensions.width}
          height={containerDimensions.height}
          batteryData={batteryData || undefined}
          siteConfigData={siteConfigData || undefined}
          showBattery={false}
          showAC={false}
          showCCS1={true}
          showCCS2={true}
          zoomIn={true}
          shouldConvertToKW={shouldConvertToKW}
        />
      );
    }

    // TYPE4: Thor V3 / BoostCharger V3 / BoostCharger -> grid + AC + CCS1 + CCS2 + main battery
    // Check for BoostCharger or Thor V3 variants (including "BoostCharger V3", "BoostCharger V3 Nordic Booster", etc.)
    if (matchesModel(modelName, ['BoostCharger', 'Thor V3', 'Thor V3 Nordic'])) {
      return (
        <BoostChargerVariantSVG
          width={containerDimensions.width}
          height={containerDimensions.height}
          batteryData={batteryData || undefined}
          siteConfigData={siteConfigData || undefined}
          showBattery={true}
          showAC={true}
          showCCS1={true}
          showCCS2={true}
          shouldConvertToKW={shouldConvertToKW}
        />
      );
    }
    
    // EXCAVATOR: Grid + Charger + Battery + Motor
    if (deviceType === 'excavator') {
      return (
        <ExcavatorVariantSVG
          width={containerDimensions.width}
          height={containerDimensions.height}
          batteryData={batteryData || undefined}
          siteConfigData={siteConfigData || undefined}
          shouldConvertToKW={shouldConvertToKW}
        />
      );
    }
    
    // If we have site config data but model name doesn't match, try to infer the type from the data structure
    if (siteConfigData) {
      const hasBattery = siteConfigData.battery && siteConfigData.battery.length > 0;
      const hasAC = siteConfigData.ac && siteConfigData.ac.length > 0;
      const hasCCS1 = siteConfigData.ccs1 && siteConfigData.ccs1.length > 0;
      const hasCCS2 = siteConfigData.ccs2 && siteConfigData.ccs2.length > 0;
      
      console.log('Inferring charger type from site config data:', {
        hasBattery,
        hasAC,
        hasCCS1,
        hasCCS2,
      });
      
      // TYPE4: Has battery, AC, CCS1, and CCS2
      if (hasBattery && hasAC && hasCCS1 && hasCCS2) {
        console.log('Inferred TYPE4 (BoostCharger/Thor V3) from data structure');
        return (
          <BoostChargerVariantSVG
            width={containerDimensions.width}
            height={containerDimensions.height}
            batteryData={batteryData || undefined}
            siteConfigData={siteConfigData || undefined}
            showBattery={true}
            showAC={true}
            showCCS1={true}
            showCCS2={true}
            shouldConvertToKW={shouldConvertToKW}
          />
        );
      }
      
      // TYPE2: Has battery, AC, CCS1, but no CCS2
      if (hasBattery && hasAC && hasCCS1 && !hasCCS2) {
        console.log('Inferred TYPE2 (Thor/Thor V2/Hummingbird V2) from data structure');
        return (
          <BoostChargerVariantSVG
            width={containerDimensions.width}
            height={containerDimensions.height}
            batteryData={batteryData || undefined}
            siteConfigData={siteConfigData || undefined}
            showBattery={true}
            showAC={true}
            showCCS1={true}
            showCCS2={false}
            shouldConvertToKW={shouldConvertToKW}
          />
        );
      }
      
      // TYPE3: Has CCS1 and CCS2, but no battery and no AC
      if (!hasBattery && !hasAC && hasCCS1 && hasCCS2) {
        console.log('Inferred TYPE3 (Mjølner/Boostpoint) from data structure');
        return (
          <BoostChargerVariantSVG
            width={containerDimensions.width}
            height={containerDimensions.height}
            batteryData={batteryData || undefined}
            siteConfigData={siteConfigData || undefined}
            showBattery={false}
            showAC={false}
            showCCS1={true}
            showCCS2={true}
            zoomIn={true}
            shouldConvertToKW={shouldConvertToKW}
          />
        );
      }
      
      // TYPE1: Has AC but no battery, no CCS1, no CCS2
      if (!hasBattery && hasAC && !hasCCS1 && !hasCCS2) {
        console.log('Inferred TYPE1 (Pro) from data structure');
        return (
          <BoostChargerVariantSVG
            width={containerDimensions.width}
            height={containerDimensions.height}
            batteryData={batteryData || undefined}
            siteConfigData={siteConfigData || undefined}
            showBattery={false}
            showAC={true}
            showCCS1={false}
            showCCS2={false}
            zoomIn={true}
            centerVertical={true}
            shouldConvertToKW={shouldConvertToKW}
          />
        );
      }
    }
    
    // For any other model, show informational message
    console.warn('No matching charger type found. Model:', modelName, 'DeviceType:', deviceType, 'HasSiteConfig:', !!siteConfigData);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 2 }}>
        <Alert severity="info">This site has not been configured.</Alert>
      </Box>
    );
  };

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        height: '100%',
        width: '100%', 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'center',
        //border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        backgroundColor: (theme) => theme.palette.background.default,
        overflow: 'hidden'
      }}
    >
      {renderSVG()}
    </Box>
  );
};

export default StaticSiteConfig;
